import { Box, Drawer } from "@mui/material"
import { Edit } from "@mui/icons-material"
import CloseIcon from "@mui/icons-material/Close"
import ItemDetails from "../../ItemDetails"
import { ComponentTableDeleteConfirmDialog } from "./ComponentTableDeleteConfirmDialog"

export type ItemPermissionFlags = {
  canRead: boolean
  canUpdate: boolean
  canDelete: boolean
  canDelegate: boolean
}

export type ComponentTableItemOverlaysProps = {
  currentItem: Record<string, unknown> | null | undefined
  currentMeta: Record<string, unknown> | null | undefined
  showDetailsDrawer: boolean
  setShowDetailsDrawer: (v: boolean) => void
  openDeleteDialog: boolean
  setOpenDeleteDialog: (v: boolean) => void
  selectedRows: unknown[]
  isItemPermissionPending: (action: "read" | "update" | "delete" | "delegate") => boolean
  getItemPermissionFlags: (uid: string | undefined) => ItemPermissionFlags
  handleOnEdit: (item?: unknown) => void
  deleteItem: () => void
  canDeleteSelected: boolean
}

export function ComponentTableItemOverlays({
  currentItem,
  currentMeta,
  showDetailsDrawer,
  setShowDetailsDrawer,
  openDeleteDialog,
  setOpenDeleteDialog,
  selectedRows,
  isItemPermissionPending,
  getItemPermissionFlags,
  handleOnEdit,
  deleteItem,
  canDeleteSelected,
}: ComponentTableItemOverlaysProps) {
  if (!currentItem || !currentMeta) return null

  const uid = (currentItem as { uid?: string }).uid

  return (
    <>
      <Drawer
        anchor="right"
        open={showDetailsDrawer}
        onClose={() => setShowDetailsDrawer(false)}
        PaperProps={{
          className: "!w-full !max-w-2xl !flex !flex-col",
        }}
      >
        <div className="flex h-full min-h-0 flex-col bg-gray-50/80 dark:bg-gray-950/30">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-800 dark:bg-gray-950">
            <h2 className="min-w-0 truncate text-lg font-semibold text-gray-900 dark:text-gray-100">
              {(currentMeta as { name?: string }).name || "Item details"}
            </h2>
            <div className="flex shrink-0 items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-gray-800 dark:bg-gray-900">
              <button
                type="button"
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium ${isItemPermissionPending("update") || !getItemPermissionFlags(uid).canUpdate
                  ? "cursor-not-allowed text-gray-400"
                  : "text-gray-800 hover:bg-white dark:text-gray-200 dark:hover:bg-gray-800"
                  }`}
                onClick={() => handleOnEdit()}
                disabled={isItemPermissionPending("update") || !getItemPermissionFlags(uid).canUpdate}
              >
                Edit
                <Edit fontSize="small" />
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-white dark:text-gray-300 dark:hover:bg-gray-800"
                onClick={() => setShowDetailsDrawer(false)}
              >
                Close
                <CloseIcon fontSize="small" />
              </button>
            </div>
          </div>
          <Box className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-5 text-left">
            <ItemDetails itemData={currentItem} meta={currentMeta} index={0} onEdit={() => { }} />
          </Box>
        </div>
      </Drawer>

      <ComponentTableDeleteConfirmDialog
        open={openDeleteDialog}
        title={`${(currentItem as { name?: string }).name || (currentItem as { title?: string }).title || "Confirm Delete"}${(currentItem as { date?: string }).date ? ` ${new Date((currentItem as { date: string }).date).toLocaleString()}` : ""}`}
        description={
          selectedRows.length > 1
            ? `Are you sure you want to delete ${selectedRows.length} items from ${(currentMeta as { name?: string }).name || "the list"}?`
            : `Are you sure you want to delete this item from ${(currentMeta as { name?: string }).name || "the list"}?`
        }
        deleteDisabled={
          !canDeleteSelected || isItemPermissionPending("delete")
        }
        onCancel={() => setOpenDeleteDialog(false)}
        onConfirmDelete={deleteItem}
      />
    </>
  )
}
